import {
  ComplianceDocument,
  DownloadCategory,
  MarketingAssetSchema,
} from '@/lib/types';
import { styled } from '@mui/material/styles';
import { FormattedMessage } from 'react-intl';
import { useRef, useState } from 'react';
import Table from '@/components/Table/Table';
import { ColumnDef } from '@/components/Table/TableHelpers';
import FileIcon from './FileIcon';
import FileTypeBadge from './FileTypeBadge';
import FileType from './FileType';
import Date from '@/components/Date/Date';
import IconButton from '@/components/IconButton/IconButton';
import Button from '@/components/Button/Button';
import Popper from '@/components/Popper/Popper';
import { MenuItem } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import MoreVertIcon from '@/assets/more-vert.svg?react';
import DownloadIcon from '@/assets/download.svg?react';
import TrashIcon from '@/assets/trash.svg?react';
import {
  deleteComplianceDocument,
  deleteMarketingAsset,
  fetchFilters,
} from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { getFileGames, groupFileGames } from './FileExplorerHelpers';
import FileGamesTooltip from './FileGamesTooltip';
import { useDownload } from '@/hooks/useDownload';
import { ga } from '@/lib/analytics';
import { isExactNameMatch } from '@/lib/searchMatch';
import ExactMatchBadge from './ExactMatchBadge';

type FileRow = ComplianceDocument | MarketingAssetSchema;

const EMPTY_MESSAGE_BY_TYPE: Record<
  'compliance' | 'marketing',
  { id: string; defaultMessage: string }
> = {
  compliance: {
    id: 'noComplianceDocuments',
    defaultMessage: 'No compliance documents have been added yet.',
  },
  marketing: {
    id: 'noMarketingAssets',
    defaultMessage: 'No marketing assets have been added yet.',
  },
};

const FileListActions = ({
  file,
  type,
  onFileDelete,
  downloadLoading,
}: {
  file: FileRow;
  type: DownloadCategory;
  onFileDelete?: () => void;
  downloadLoading?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const { download, loading } = useDownload();

  const handleDownloadClick = () => {
    ga.trackAssetDownload({
      asset_id: file.id,
      asset_name: file.name,
      asset_format: file.extension,
    });
    download({
      category: type,
      file_ids: [file.id],
      entity_name: file.name,
      extension: file.extension,
    });
  };

  const handleDeleteClick = () => {
    const onSuccess = () => {
      onFileDelete?.();
      setIsOpen(false);
    };
    if (type === 'compliance_documents') {
      deleteComplianceDocument(file.id).then(onSuccess);
    } else if (type === 'marketing_assets') {
      deleteMarketingAsset(file.id).then(onSuccess);
    }
  };

  return (
    <ActionsRoot>
      <Button
        variant="text"
        onClick={handleDownloadClick}
        aria-label="Download"
        loading={loading || downloadLoading}
      >
        <DownloadIcon />
        <FormattedMessage id="download" defaultMessage="Download" />
      </Button>
      {onFileDelete && (
        <>
          <ActionsIconWrapper ref={actionsRef}>
            <IconButton
              onClick={() => setIsOpen((v) => !v)}
              aria-label="File actions"
            >
              <MoreVertIcon />
            </IconButton>
          </ActionsIconWrapper>
          <Popper
            aria-label="File actions popper"
            open={isOpen}
            anchorEl={actionsRef.current}
            onClose={() => setIsOpen(false)}
          >
            <PopperBody>
              <StyledMenuItem onClick={handleDeleteClick}>
                <TrashIcon />{' '}
                <FormattedMessage id="delete" defaultMessage="Delete" />
              </StyledMenuItem>
            </PopperBody>
          </Popper>
        </>
      )}
    </ActionsRoot>
  );
};

const FileListTable = ({
  files,
  loading,
  loadingNextPage,
  type = 'compliance',
  selected,
  onSelect,
  onFileDelete,
  downloadLoading,
  hasActiveSearchOrFilter,
  exactMatchQuery,
}: {
  files: FileRow[];
  loading?: boolean;
  loadingNextPage?: boolean;
  type?: 'compliance' | 'marketing';
  selected?: number[];
  onSelect?: (selected: number[]) => void;
  onFileDelete?: () => void;
  downloadLoading?: boolean;
  hasActiveSearchOrFilter?: boolean;
  exactMatchQuery?: string;
}) => {
  const downloadCategory: DownloadCategory =
    type === 'compliance' ? 'compliance_documents' : 'marketing_assets';

  const { data: filters } = useQuery({
    queryKey: ['filters'],
    queryFn: fetchFilters,
  });

  const columns: ColumnDef<FileRow>[] = [
    {
      id: 'name',
      label: <FormattedMessage id="name" defaultMessage="Name" />,
      width: '30%',
      render: (file) => (
        <NameCell>
          <FileIcon fileType={file.extension} size={20} />
          <FileName title={file.name}>
            {file.extension === 'pdf' && file.preview_url ? (
              <a
                href={file.preview_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {file.name}
              </a>
            ) : (
              file.name
            )}
          </FileName>
          {exactMatchQuery &&
          isExactNameMatch(
            file.name,
            exactMatchQuery,
            file.extension,
          ) ? (
            <ExactMatchBadge />
          ) : null}
        </NameCell>
      ),
    },
    {
      id: 'type',
      label: <FormattedMessage id="type" defaultMessage="Type" />,
      width: '10%',
      render: (file) => (
        <FileTypeBadge extension={file.extension} file={file} />
      ),
    },
    {
      id: 'fileType',
      label: (
        <FormattedMessage id="fileType" defaultMessage="File Type" />
      ),
      width: '15%',
      render: (file) => <FileType file={file} />,
    },
    {
      id: 'secondary',
      label: <FormattedMessage id="games" defaultMessage="Games" />,
      width: '20%',
      render: (file) => {
        const gameRows = getFileGames(filters?.games, file);
        if (!gameRows.length) {
          return null;
        }
        return (
          <FileGamesTooltip groups={groupFileGames(gameRows)}>
            <EllipsisCell>
              {gameRows.map(({ name }) => name).join(', ')}
            </EllipsisCell>
          </FileGamesTooltip>
        );
      },
    },
    {
      id: 'date',
      label: (
        <FormattedMessage id="dateAdded" defaultMessage="Date" />
      ),
      width: '10%',
      render: (file) => <Date value={file.updated_at} />,
    },
    {
      id: 'actions',
      label: (
        <FormattedMessage id="actions" defaultMessage="Actions" />
      ),
      sortable: false,
      align: 'right',
      width: '15%',
      render: (file) => (
        <FileListActions
          file={file}
          type={downloadCategory}
          onFileDelete={onFileDelete}
          downloadLoading={downloadLoading}
        />
      ),
    },
  ];

  if (!loading && files.length === 0) {
    const emptyMessage = EMPTY_MESSAGE_BY_TYPE[type];
    return (
      <EmptyContainer>
        {hasActiveSearchOrFilter ? (
          <FormattedMessage
            id="noSearchResults"
            defaultMessage="No search results."
          />
        ) : (
          <FormattedMessage
            id={emptyMessage.id}
            defaultMessage={emptyMessage.defaultMessage}
          />
        )}
      </EmptyContainer>
    );
  }

  return (
    <Root>
      <Table<FileRow>
        rows={files}
        columns={columns}
        loading={loading}
        selectedRows={selected}
        onSelectedRowsChange={onSelect}
        onRequestSort={() => {}}
      />
      {!loading && loadingNextPage && (
        <LoaderRow>
          <CircularProgress size={24} />
        </LoaderRow>
      )}
    </Root>
  );
};

const Root = styled('div')({
  width: '100%',
});

const NameCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  minWidth: 0,
});

const FileName = styled('div')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: theme.palette.textPrimaryBlack,

  a: {
    color: theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

const EllipsisCell = styled('div')({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const ActionsRoot = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: 4,
});

const ActionsIconWrapper = styled('div')({
  display: 'flex',
});

const PopperBody = styled('div')(({ theme }) => ({
  width: 180,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.radii.xs,
  backgroundColor: theme.palette.white,
  padding: '8px 0px',
  boxShadow:
    '0px 10px 32px -4px #1319271A, 0px 6px 14px -6px #1319271F',
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  fontSize: 14,
  lineHeight: '20px',
  fontWeight: 400,
  color: theme.palette.textPrimaryBlack,
  padding: '12px 16px',
  cursor: 'pointer',

  svg: {
    path: {
      stroke: theme.palette.textPrimaryBlack,
    },
  },
}));

const EmptyContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.grey500,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '24px',
}));

const LoaderRow = styled('div')(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(4),
  display: 'flex',
  justifyContent: 'center',
  borderLeft: `1px solid ${theme.palette.grey200}`,
  borderRight: `1px solid ${theme.palette.grey200}`,
  borderBottom: `1px solid ${theme.palette.grey200}`,
}));

export default FileListTable;
